<template>
  <!-- FT-074: витрина доп-услуг для арендатора — что подключено и что по чём.
       DEC-32: superadmin/admin могут сами подключать/отключать add-on'ы (canManage). -->
  <v-card v-if="hasPlan" flat class="tx-card mt-4 mx-3">
    <v-card-text class="pa-5">
      <div class="d-flex align-center mb-3">
        <v-icon left color="primary">mdi-shape-plus-outline</v-icon>
        <span class="text-subtitle-1 font-weight-medium">{{ $t("billing_overview.title") }}</span>
      </div>

      <!-- Подключённые доп-услуги (строки, всегда видны) -->
      <div v-for="addon in addons" :key="`a-${addon.code}`" class="tx-addon d-flex align-center py-2">
        <div class="tx-row-ico mr-3"><v-icon small>{{ addonIcon(addon.code) }}</v-icon></div>
        <div class="flex-grow-1" style="min-width:0">
          <div class="text-body-2">
            {{ codeLabel(addon.code) }}<span v-if="addon.quantity > 1" class="grey--text"> × {{ addon.quantity }}</span>
            <!-- FT-081 P3-1: строка ещё триальная — бейдж рядом с названием, чтобы не путали с уже оплаченной -->
            <v-chip v-if="isTrialRow(addon)" x-small color="info" outlined class="ml-2">
              {{ $t("crm.trial.badge") }}
            </v-chip>
          </div>
          <div class="text-caption grey--text">
            {{ addonLineText(addon) }}<span v-if="isIncrementable(addon.code) && addon.quantity > 0"> · +{{ addon.quantity }} {{ $t("billing_overview.seats_qty") }}</span>
          </div>
        </div>
        <!-- FT-081 P2-1: истёкший неподтверждённый триал уже погасил доступ (в т.ч. на CRM-страницу,
             откуда обычно подтверждают) — даём путь оплатить прямо здесь, пока строка ещё active. -->
        <v-btn
          v-if="canManage && isTrialRow(addon) && !addon.trial_confirmed_at"
          small
          outlined
          color="primary"
          class="mr-2"
          :disabled="busy"
          :loading="confirmingTrialId === addon.id"
          data-cy="crm-trial-confirm-billing-btn"
          @click="confirmTrial(addon)"
        >
          {{ $t("crm.trial.confirm_btn") }}
        </v-btn>
        <v-btn v-if="canManage && isIncrementable(addon.code)" text small color="primary" :disabled="busy" @click="askAddSeats(addon)">
          {{ $t("billing_overview.add_more") }}
        </v-btn>
        <v-btn v-if="canManage" text small color="error" :disabled="busy" @click="askDisable(addon)">
          {{ $t("billing_overview.disconnect") }}
        </v-btn>
      </div>

      <!-- Доступные к подключению (ещё не активны) -->
      <div v-for="item in connectableCatalog" :key="`c-${item.code}`" class="tx-addon d-flex align-center py-2">
        <div class="tx-row-ico mr-3"><v-icon small>{{ addonIcon(item.code) }}</v-icon></div>
        <div class="flex-grow-1" style="min-width:0">
          <div class="text-body-2">{{ codeLabel(item.code) }}</div>
          <div class="text-caption grey--text">{{ catalogPriceText(item) }}</div>
        </div>
        <!-- FT-081: CRM с доступным триалом — первичная кнопка «Попробовать», обычная покупка вторична -->
        <v-btn
          v-if="canManage && isCrmTrialOffer(item)"
          small
          color="primary"
          class="mr-2"
          :disabled="busy"
          :loading="trialBusy"
          data-cy="crm-trial-start-btn"
          @click="startCrmTrial"
        >
          {{ $t("crm.trial.start_btn") }}
        </v-btn>
        <v-btn v-if="canManage" :text="isCrmTrialOffer(item)" small color="primary" :disabled="busy" @click="askEnable(item)">
          <v-icon left small>mdi-plus</v-icon>{{ $t("billing_overview.connect") }}
        </v-btn>
      </div>

      <div v-if="!addons.length && !connectableCatalog.length" class="grey--text text-body-2 py-2">
        {{ $t("billing_overview.no_addons") }}
      </div>

      <!-- Скидки -->
      <template v-if="discounts.length">
        <v-divider class="my-3"></v-divider>
        <div class="text-caption grey--text mb-2">{{ $t("billing_overview.your_discounts") }}</div>
        <div v-for="(d, i) in discounts" :key="`d-${i}`" class="d-flex justify-space-between align-center py-1">
          <span class="text-body-2">{{ scopeLabel(d.scope) }}</span>
          <v-chip x-small color="success" outlined>{{ discountText(d) }}</v-chip>
        </div>
      </template>
    </v-card-text>

    <!-- Деньги-warning перед мутацией (DEC-32). -->
    <v-dialog v-model="confirmDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ confirmTitle }}</v-card-title>
        <v-card-text>
          {{ confirmText }}
          <!-- FT-077: степпер количества докупаемых мест (для extra_user) -->
          <template v-if="showQtyStepper">
            <div class="d-flex align-center justify-center mt-4">
              <v-btn icon small :disabled="busy || qty <= 1" @click="qty = Math.max(1, qty - 1)">
                <v-icon>mdi-minus-circle-outline</v-icon>
              </v-btn>
              <div class="mx-4 text-h6 font-weight-bold">{{ qty }}</div>
              <v-btn icon small :disabled="busy" @click="qty = qty + 1">
                <v-icon>mdi-plus-circle-outline</v-icon>
              </v-btn>
            </div>
            <div class="text-center text-caption grey--text">{{ $t("billing_overview.seats_qty") }}</div>
            <div v-if="stepperTotalText" class="text-center text-body-2 font-weight-medium mt-2">
              {{ stepperTotalText }}
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="busy" @click="confirmDialog = false">
            {{ $t("billing_overview.cancel") }}
          </v-btn>
          <v-btn
            :color="mode === 'disable' ? 'error' : 'primary'"
            :loading="busy"
            @click="confirmAction"
          >
            {{ $t("billing_overview.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "BillingOverview",
  props: {
    mainCompany: { type: Object, default: () => ({}) },
    // DEC-32: показывать ли кнопки управления (superadmin/admin). Сервер всё равно гейтит ролью.
    canManage: { type: Boolean, default: false },
  },
  data() {
    return {
      confirmDialog: false,
      mode: "enable", // 'enable' | 'add' | 'disable'
      target: null, // item (enable) или addon (add/disable)
      busy: false,
      qty: 1, // FT-077: количество мест при подключении/докупке extra_user
      trialBusy: false, // FT-081: отдельный флаг для CTA триала CRM
      confirmingTrialId: null, // FT-081 P2-1: id строки, для которой сейчас идёт confirm
    };
  },
  computed: {
    // FT-081 P3-3: единый источник вместо дублирующего локального computed —
    // getters.crm.crmTrialAvailable уже читает то же поле mc.crm_trial_available.
    ...mapGetters("crm", ["crmTrialAvailable"]),
    hasPlan() {
      return !!(this.mainCompany && this.mainCompany.plan);
    },
    currency() {
      const p = this.mainCompany && this.mainCompany.plan;
      return (p && p.currency) || (this.mainCompany && this.mainCompany.currency) || "RUB";
    },
    addons() {
      return (this.mainCompany && this.mainCompany.company_addons) || [];
    },
    discounts() {
      return (this.mainCompany && this.mainCompany.company_discounts) || [];
    },
    catalog() {
      return (this.mainCompany && this.mainCompany.billing_catalog) || [];
    },
    // FT-077 редизайн: доступные к подключению (ещё не активны + self-serviceable).
    connectableCatalog() {
      return this.catalog.filter((item) => !this.isActive(item.code) && this.isConnectable(item));
    },
    confirmTitle() {
      const key =
        this.mode === "disable"
          ? "billing_overview.confirm_disconnect_title"
          : this.mode === "add"
            ? "billing_overview.confirm_add_title"
            : "billing_overview.confirm_connect_title";
      return this.$t(key);
    },
    confirmText() {
      if (!this.target) return "";
      if (this.mode === "disable") {
        return this.$t("billing_overview.confirm_disconnect_text", {
          name: this.codeLabel(this.target.code),
        });
      }
      if (this.mode === "add") {
        return this.$t("billing_overview.confirm_add_text", { name: this.codeLabel(this.target.code) });
      }
      return this.$t("billing_overview.confirm_connect_text", {
        name: this.codeLabel(this.target.code),
        // FT-081: бесплатная позиция (CRM на Премиуме, price_cents=0) — «включено», не «0 ₽/мес».
        price: Number(this.target.price_cents) === 0
          ? this.$t("billing_overview.included")
          : `${this.formatCents(this.target.price_cents)}${this.$t("billing_overview.per_month")}`,
      });
    },
    // FT-077: степпер количества — только для incrementable (extra_user) при подключении/докупке.
    showQtyStepper() {
      return (
        (this.mode === "enable" || this.mode === "add") &&
        !!this.target &&
        this.isIncrementable(this.target.code)
      );
    },
    // Цена/место из каталога тарифа (list-цена; фактическое списание маржинально/пропорц. — на бэке).
    unitCents() {
      const code = this.target && this.target.code;
      const item = this.catalog.find((c) => c.code === code);
      return item ? Number(item.price_cents) || 0 : 0;
    },
    stepperTotalText() {
      if (!this.showQtyStepper || this.unitCents <= 0) return "";
      const total = this.formatCents(this.unitCents * this.qty);
      return `${this.qty} × ${this.formatCents(this.unitCents)} = ${total}${this.$t("billing_overview.per_month")}`;
    },
  },
  methods: {
    // price_cents → деньги в валюте тира (RUB без копеек, иначе 2 знака).
    formatCents(cents) {
      const value = (Number(cents) || 0) / 100;
      const fixed = this.currency === "RUB" ? value.toFixed(0) : value.toFixed(2);
      return `${fixed} ${this.currency}`;
    },
    codeLabel(code) {
      const key = `billing_overview.addon_${code}`;
      const translated = this.$t(key);
      // Нет перевода → показываем сам код (на случай новых billable-кодов).
      return translated === key ? code : translated;
    },
    scopeLabel(scope) {
      if (scope === "ALL") return this.$t("billing_overview.discount_on_all");
      return this.codeLabel(scope);
    },
    discountText(d) {
      if (d.kind === "percent") return `−${Number(d.value)}%`;
      return `−${Number(d.value)} ${d.currency || this.currency}`;
    },
    // Истинный месячный заряд приходит с бэка (monthly_cents — mirror формулы списания
    // max(0, rate×qty − amount_cents), включая amount-скидку). НЕ пересчитываем из каталога
    // (тот — list-цена тира без amount-скидки) — иначе цена расходилась бы с фактом.
    addonLineText(addon) {
      if (addon.monthly_cents === null || addon.monthly_cents === undefined) return "—";
      if (Number(addon.monthly_cents) === 0) return this.$t("billing_overview.included");
      return `${this.formatCents(addon.monthly_cents)} ${this.$t("billing_overview.per_month")}`;
    },
    catalogPriceText(item) {
      if (item.pricing_model === "included" || Number(item.price_cents) === 0) {
        return this.$t("billing_overview.included");
      }
      return `${this.formatCents(item.price_cents)} ${this.$t("billing_overview.per_month")}`;
    },
    isActive(code) {
      return this.addons.some((a) => a.code === code);
    },
    // FT-081 P3-1/P2-1: строка активного add-on'а всё ещё в триале (не начислялась оплата).
    isTrialRow(addon) {
      return !!(addon && addon.trial_ends_at);
    },
    // Self-serviceable = flat_period (серверный self_serviceable? смотрит ТОЛЬКО pricing_model).
    // included / per_use (мессенджеры) — не подключают. FT-081: допускаем price_cents = 0 (>= 0):
    // CRM на Премиуме бесплатен (crm_cost 0), но подключить всё равно нужно (ASM-02); прежний
    // `> 0` ошибочно прятал его из каталога. `>= 0` заодно отсекает мусорную/undefined-цену.
    isConnectable(item) {
      return item.pricing_model === "flat_period" && Number(item.price_cents) >= 0;
    },
    // FT-077 редизайн: иконка строки доп-услуги.
    addonIcon(code) {
      if (code === "extra_user") return "mdi-account-plus";
      if (code === "crm") return "mdi-view-dashboard-outline";
      return "mdi-cube-outline";
    },
    askEnable(item) {
      this.mode = "enable";
      this.target = item;
      this.qty = 1;
      this.confirmDialog = true;
    },
    // FT-081: показывать CTA «Попробовать 14 дней» только для CRM-позиции каталога,
    // когда бэк сообщает crm_trial_available (тариф предлагает + триал не брали + не подключён).
    isCrmTrialOffer(item) {
      return item.code === "crm" && this.crmTrialAvailable;
    },
    startCrmTrial() {
      if (this.trialBusy) return;
      this.trialBusy = true;
      this.axios
        .post(
          "/api/v1/company_addons/crm_trial",
          {},
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        )
        // FT-081: рефрешим И main_company (crm_active), И currentUser (crm_access_enabled грантится
        // на старте) — иначе переход в CRM после старта отсюда упрётся в accessOn-гейт → access_denied.
        .then(() => Promise.all([this.refreshMainCompany(), this.refreshCurrentUser()]))
        .then(() => {
          this.$notify(this.$t("crm.trial.started"), "", "success");
          this.$emit("changed");
        })
        .catch((error) => this.notifyTrialError(error))
        .finally(() => (this.trialBusy = false));
    },
    // FT-081: GET /me.json → setCurrentUser (образец AppHeader). Тихо резолвим на ошибке.
    refreshCurrentUser() {
      return this.axios
        .get("/me.json", { headers: { Authorization: this.$store.getters.getAuthToken } })
        .then((response) => this.$store.commit("setCurrentUser", response.data))
        .catch(() => {});
    },
    // FT-081 P2-1: confirm прямо из «Кабинета счёта» — нужен, когда неподтверждённый истёкший
    // триал уже погасил доступ к CRM-странице (там обычно живёт CrmTrialBanner-кнопка).
    confirmTrial(addon) {
      if (!addon || this.confirmingTrialId) return;
      this.confirmingTrialId = addon.id;
      this.axios
        .post(
          `/api/v1/company_addons/${addon.id}/confirm`,
          {},
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        )
        .then(() => this.refreshMainCompany())
        .then(() => {
          this.$notify(this.$t("crm.trial.confirmed"), "", "success");
          this.$emit("changed");
        })
        .catch((error) => this.notifyTrialError(error))
        .finally(() => (this.confirmingTrialId = null));
    },
    // FT-081: тот же паттерн рефреша, что reloadBilling в Transactions.vue — GET main_company → setMainCompany.
    refreshMainCompany() {
      const mainCompanyId = this.mainCompany && this.mainCompany.id;
      if (!mainCompanyId) return Promise.resolve();
      return this.axios
        .get(`/api/v1/main_companies/${mainCompanyId}`, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then((response) => this.$store.commit("setMainCompany", response.data));
    },
    notifyTrialError(error) {
      const data = error && error.response && error.response?.data;
      const code = data && data.error;
      // Тот же guard, что ниже в notifyAddonError: полем error приходит и 403
      // «Not authorized» от authorize_self_service!, а vue-i18n на неизвестный
      // ключ возвращает keypath — в тосте была строка «crm.trial.errors.Not authorized».
      const key = code && `crm.trial.errors.${code}`;
      const msg = (key && this.$te(key) && this.$t(key)) || code || this.$t("billing_overview.action_error");
      this.$notify(this.$t("errors.error"), msg, "error");
    },
    // FT-077: incrementable add-on (extra_user) — можно докупить места.
    isIncrementable(code) {
      return code === "extra_user";
    },
    askAddSeats(addon) {
      this.mode = "add";
      this.target = addon;
      this.qty = 1;
      this.confirmDialog = true;
    },
    askDisable(addon) {
      this.mode = "disable";
      this.target = addon;
      this.confirmDialog = true;
    },
    confirmAction() {
      if (this.mode === "disable") {
        this.doDisable();
      } else {
        this.doEnable();
      }
    },
    doEnable() {
      this.busy = true;
      this.axios
        .post(
          "/api/v1/company_addons",
          // FT-077: quantity=N. Бэк создаёт addon (первое подключение) или инкрементит (докупка мест).
          { code: this.target.code, quantity: this.isIncrementable(this.target.code) ? this.qty : 1 },
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        )
        .then(() => {
          this.$notify(this.$t("billing_overview.connected_ok"), "", "success");
          this.confirmDialog = false;
          this.$emit("changed");
        })
        .catch((error) => this.notifyError(error))
        .finally(() => (this.busy = false));
    },
    doDisable() {
      this.busy = true;
      this.axios
        .delete(`/api/v1/company_addons/${this.target.id}`, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then(() => {
          this.$notify(this.$t("billing_overview.disconnected_ok"), "", "success");
          this.confirmDialog = false;
          this.$emit("changed");
        })
        .catch((error) => this.notifyError(error))
        .finally(() => (this.busy = false));
    },
    notifyError(error) {
      const data = error && error.response && error.response?.data;
      // FT-077: недостаточно баланса для подключения доп-услуги → понятное предупреждение (не «ошибка»).
      if (data && data.error === "insufficient_balance") {
        this.$notify(
          this.$t("seat_paywall.insufficient_balance_title"),
          this.$t("seat_paywall.insufficient_balance_text"),
          "warning",
          8000
        );
        return;
      }
      // FT-081 P3-4: код ошибки может прилетать из общего crm.trial.errors.* namespace
      // (напр. crm_already_active — обычная покупка CRM, когда триал/подписка уже активны) —
      // показываем локализованный текст вместо сырого кода, если ключ реально существует.
      const trialErrorKey = data && data.error && `crm.trial.errors.${data.error}`;
      const msg =
        (trialErrorKey && this.$te(trialErrorKey) && this.$t(trialErrorKey)) ||
        (data && data.error) ||
        this.$t("billing_overview.action_error");
      this.$notify(this.$t("errors.error"), msg, "error");
    },
  },
};
</script>

<style scoped>
/* FT-077 редизайн «Кабинет счёта»: залитая скруглённая карточка (outlined невидим на тёмной теме) */
.tx-card {
  border-radius: 16px !important;
}
.tx-card.theme--dark {
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.tx-card.theme--light {
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.tx-row-ico {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: rgba(128, 128, 128, 0.14);
}
.tx-addon + .tx-addon {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}
</style>
